import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, FormControlLabel, Checkbox } from '@mui/material';
import { Button, Autocomplete, TextField, Dialog, FakeText } from '@totalsoft/rocket-ui';
import { withTranslation } from 'react-i18next';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { get, set } from '@totalsoft/change-tracking-react';
import PhoneNumberField from 'components/common/inputs/PhoneNumberTextField';

const SimulatorPFClientComponent = ({ t, partnerLens, relations, loadGDPR, gdpr, validation, editPartnerDisabled }) => {
  const generalData = useMemo(() => partnerLens.generalData, [partnerLens.generalData]);
  const contactData = useMemo(() => partnerLens.contactData, [partnerLens.contactData]);

  const [openGDPR, setOpenGDPR] = useState(false);
  const handleClientAgreesWithPersonalDataUse = useCallback(
    value => {
      if (value) {
        loadGDPR();
        setOpenGDPR(value);
      }
    },
    [loadGDPR]
  );

  const handleGDPRPositive = useCallback(() => {
    (partnerLens.agreesWithPersonalDataUse |> set)(true);
    setOpenGDPR(false);
  }, [partnerLens.agreesWithPersonalDataUse]);

  const handleGDPRClose = useCallback(() => {
    setOpenGDPR(false);
  }, []);

  const handleFirstNameChange = useCallback(
    firstName => {
      (partnerLens.generalData.firstName |> set)(firstName);
      (partnerLens.generalData.partnerName |> set)(`${firstName} ${partnerLens.generalData.lastName |> get}`);
      (partnerLens.contactData.personName |> set)(`${firstName} ${partnerLens.generalData.lastName |> get}`);
    },
    [partnerLens]
  );

  const handleLastNameChange = useCallback(
    lastName => {
      (partnerLens.generalData.lastName |> set)(lastName);
      (partnerLens.generalData.partnerName |> set)(`${partnerLens.generalData.firstName |> get} ${lastName}`);
      (partnerLens.contactData.personName |> set)(`${partnerLens.generalData.firstName |> get} ${lastName}`);
    },
    [partnerLens]
  );

  return (
    <>
      <Grid item xs={12} sm={6}>
        <FormControlLabel
          control={
            <Checkbox
              color='primary'
              name='agreesWithPersonalDataUse'
              checked={partnerLens.agreesWithPersonalDataUse |> get}
              onChange={handleClientAgreesWithPersonalDataUse}
              disabled={partnerLens.agreesWithPersonalDataUse |> get || editPartnerDisabled}
            />
          }
          label={t('Simulator.Client.AgreesWithPersonalDataUse')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          name='uniqueId'
          label={t('Simulator.Client.PF.PersonalIdentifier')}
          value={generalData.uniqueId |> get}
          error={!isValid(validation.generalData.uniqueId)}
          helperText={getErrors(validation.generalData.uniqueId)}
          onChange={generalData.uniqueId |> set}
          disabled={!(partnerLens.agreesWithPersonalDataUse |> get) || editPartnerDisabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          fullWidth
          isClearable={false}
          label={t('Simulator.Client.Relation')}
          simpleValue={true}
          valueKey='id'
          value={generalData.relationId |> get}
          name='relation'
          options={relations}
          placeholder={t('Simulator.Client.NoRelation')}
          onChange={generalData.relationId |> set}
          disabled={!(partnerLens.agreesWithPersonalDataUse |> get) || editPartnerDisabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          value={generalData.firstName |> get}
          name='firstName'
          label={t('Simulator.Client.FirstName')}
          onChange={handleFirstNameChange}
          error={!isValid(validation.generalData.firstName)}
          helperText={getErrors(validation.generalData.firstName)}
          disabled={!(partnerLens.agreesWithPersonalDataUse |> get) || editPartnerDisabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          value={generalData.lastName |> get}
          name='lastName'
          label={t('Simulator.Client.LastName')}
          onChange={handleLastNameChange}
          error={!isValid(validation.generalData.lastName)}
          helperText={getErrors(validation.generalData.lastName)}
          disabled={!(partnerLens.agreesWithPersonalDataUse |> get) || editPartnerDisabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <PhoneNumberField
          sx={{
            '& .MuiInput-input': {
              padding: '2px'
            }
          }}
          fullWidth
          variant='standard'
          size='small'
          value={contactData.phone |> get}
          label={t('Simulator.Client.MobilePhone')}
          onChange={contactData.phone |> set}
          error={!isValid(validation.contactData.phone)}
          helperText={getErrors(validation.contactData.phone)}
          disabled={!(partnerLens.agreesWithPersonalDataUse |> get) || editPartnerDisabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          value={contactData.email |> get}
          name='email'
          label={t('Simulator.Client.Email')}
          onChange={contactData.email |> set}
          error={!isValid(validation.contactData.email)}
          helperText={getErrors(validation.contactData.email)}
          disabled={!(partnerLens.agreesWithPersonalDataUse |> get) || editPartnerDisabled}
        />
      </Grid>
      <Dialog
        open={openGDPR}
        onClose={handleGDPRClose}
        aria-labelledby='gdpr-title'
        aria-describedby='gdpr-description'
        title={t('Simulator.Client.GDPR.Title')}
        content={!gdpr.isLoaded ? <FakeText /> : <div dangerouslySetInnerHTML={{ __html: gdpr.text }} />}
        actions={
          <>
            <Button disabled={!gdpr.isLoaded} color='primary' autoFocus onClick={handleGDPRPositive}>
              {t('Simulator.Client.GDPR.AcceptButton')}
            </Button>
            <Button disabled={!gdpr.isLoaded} color='primary' onClick={handleGDPRClose}>
              {t('Simulator.Client.GDPR.RejectButton')}
            </Button>
          </>
        }
      />
    </>
  );
};

SimulatorPFClientComponent.propTypes = {
  t: PropTypes.func.isRequired,
  relations: PropTypes.array,
  loadGDPR: PropTypes.func.isRequired,
  gdpr: PropTypes.object,
  partnerLens: PropTypes.object.isRequired,
  validation: PropTypes.object.isRequired,
  editPartnerDisabled: PropTypes.bool.isRequired
};

export default withTranslation()(SimulatorPFClientComponent);
